import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import EditableTable from "../components/EditableTable";
import PersistentTextarea from "../components/PersistentTextarea";
import QrPrintModal, { QrItem } from "../components/QrPrintModal";
import ResizableSplit from "../components/ResizableSplit";
import { useViewWidth } from "../components/ViewWidthSlider";
import {
  HEADERS,
  COL,
  DEFAULT_QR_SETTINGS,
  QR_PAGE_SIZES,
  QR_ORIENTATIONS,
  appendRows,
  computeDisplayName,
  deleteRow,
  displayNameFormula,
  ensureSpreadsheet,
  mergePhotoFileIds,
  parsePhotoFileIds,
  readAllRows,
  readQrSettings,
  reportUrl,
  spreadsheetUrl,
  updateCell,
  writeQrSettings,
  writeWatermarkFileId,
} from "../api/sheets";
import { deletePhotosByIds, ensureAssetIcon, ensurePicturesFolder, ensureMapsFolder, ensureWatermark, publicImageUrl, uploadDispenserPhotos } from "../api/drive";
import { applyWatermark } from "../utils/watermark";
import { formEditUrl } from "../api/forms";
import { useAdvancedMode } from "../utils/advancedMode";
import { useT } from "../i18n";
import { QrSettings, Row } from "../types/sheets";

const SVG_NS = "http://www.w3.org/2000/svg";

function escapeXml(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isCjk(ch: string): boolean {
  return /[　-〿぀-ゟ゠-ヿ㐀-䶿一-鿿＀-￯]/.test(ch);
}

function wrapMessage(text: string, maxHalfWidths: number): string[] {
  const lines: string[] = [];
  for (const rawLine of String(text ?? "").split(/\r?\n/)) {
    if (!rawLine) { lines.push(""); continue; }
    let cur = "";
    let curW = 0;
    for (const ch of rawLine) {
      const chW = isCjk(ch) ? 2 : 1;
      if (curW + chW > maxHalfWidths && cur) {
        const lastSpace = cur.lastIndexOf(" ");
        if (lastSpace > 0 && !isCjk(ch)) {
          lines.push(cur.slice(0, lastSpace));
          cur = cur.slice(lastSpace + 1);
          curW = 0;
          for (const c of cur) curW += isCjk(c) ? 2 : 1;
        } else {
          lines.push(cur);
          cur = "";
          curW = 0;
        }
      }
      if (ch === " " && !cur) continue;
      cur += ch;
      curW += chW;
    }
    if (cur) lines.push(cur);
  }
  return lines;
}

interface QrCardParams {
  id: string;
  url: string;
  message: string;
  label: string;
  showLabel: boolean;
  installLocation: string;
  showInstallLocation: boolean;
}

async function buildQrCardSvg({ id, url, message, label, showLabel, installLocation, showInstallLocation }: QrCardParams): Promise<string> {
  const qr = QRCode.create(url, { errorCorrectionLevel: "M" });
  const count = qr.modules.size;
  const bits = qr.modules.data;
  const QUIET = 2;
  const qrDim = count + QUIET * 2;
  let modulePath = "";
  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (bits[r * count + c]) modulePath += `M${c + QUIET} ${r + QUIET}h1v1h-1z`;
    }
  }
  const qrViewBox = `0 0 ${qrDim} ${qrDim}`;
  const qrInner = `<rect width="${qrDim}" height="${qrDim}" fill="#fff"/><path d="${modulePath}" fill="#000"/>`;

  const W = 400;
  const PAD = 10;
  const QR_SIZE = 128;
  const QR_X = W - PAD - QR_SIZE;
  const QR_Y = 22;
  const GAP = 12;
  const MSG_RIGHT = QR_X - GAP;
  const MSG_LEFT = PAD;
  const MSG_WIDTH = MSG_RIGHT - MSG_LEFT;
  const LINE_RATIO = 1.5;

  let fontSize = 18;
  let lines: string[] = [];
  if (message) {
    const sourceLines = String(message).split(/\r?\n/);
    const lineHW = (s: string) => { let w = 0; for (const c of s) w += isCjk(c) ? 2 : 1; return w; };
    const maxLineHW = Math.max(1, ...sourceLines.map(lineHW));
    const widthFit = (MSG_WIDTH * 2) / maxLineHW;
    const heightFit = QR_SIZE / (sourceLines.length * LINE_RATIO);
    fontSize = Math.max(8, Math.min(28, Math.min(widthFit, heightFit)));
    const maxHW = Math.max(4, Math.floor(MSG_WIDTH / (fontSize * 0.5)));
    lines = wrapMessage(message, maxHW);
  }
  const lineHeight = fontSize * LINE_RATIO;
  const totalTextH = lines.length * lineHeight;
  const textTopY = QR_Y + (QR_SIZE - totalTextH) / 2;
  const labelParts: string[] = [];
  if (showLabel && label) labelParts.push(String(label));
  if (showInstallLocation && installLocation) labelParts.push(String(installLocation));
  const labelText = labelParts.join("　");
  const qrBottom = QR_Y + QR_SIZE;
  const H = labelText ? qrBottom + 24 : Math.max(qrBottom + 8, 160);

  const idText = `<text x="${W - PAD}" y="18" text-anchor="end" font-family="sans-serif" font-size="11" font-weight="600" fill="#374151">ID: ${escapeXml(id)}</text>`;
  const tspans = lines.map((ln, i) => `<tspan x="${MSG_LEFT}" dy="${i === 0 ? 0 : lineHeight}">${escapeXml(ln) || " "}</tspan>`).join("");
  const msgText = lines.length ? `<text x="${MSG_LEFT}" y="${textTopY + fontSize}" font-family="sans-serif" font-size="${fontSize}" font-weight="500" fill="#111827">${tspans}</text>` : "";
  const qrSvg = `<svg x="${QR_X}" y="${QR_Y}" width="${QR_SIZE}" height="${QR_SIZE}" viewBox="${qrViewBox}">${qrInner}</svg>`;
  const labelSvg = labelText ? `<text x="${W / 2}" y="${qrBottom + 18}" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#1f2937">${escapeXml(labelText)}</text>` : "";

  return `<svg xmlns="${SVG_NS}" viewBox="0 0 ${W} ${H}" width="100%" preserveAspectRatio="xMidYMid meet" shape-rendering="crispEdges">${idText}${msgText}${qrSvg}${labelSvg}</svg>`;
}

export default function SheetView() {
  const t = useT();
  const advanced = useAdvancedMode();
  const { maxWidth, slider } = useViewWidth("viewWidth.sheet");
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const notify = (text: string, tone = "success") => { setStatus(text); setStatusTone(tone); };
  const notifyError = (text: string) => { setStatus(text); setStatusTone("error"); };
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [qrSettings, setQrSettings] = useState<QrSettings>(() => {
    const stored = localStorage.getItem("mew_qr_show_label");
    const storedLoc = localStorage.getItem("mew_qr_show_install_location");
    return {
      ...DEFAULT_QR_SETTINGS,
      ...(stored !== null ? { showLabel: stored === "true" } : {}),
      ...(storedLoc !== null ? { showInstallLocation: storedLoc === "true" } : {}),
    };
  });
  const [qrItems, setQrItems] = useState<QrItem[] | null>(null);
  const [watermarkId, setWatermarkId] = useState<string | null>(null);
  const [addWatermark] = useState(() => localStorage.getItem("mew_add_watermark") !== "false");
  const [initialLoading, setInitialLoading] = useState(true);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoTargetIdRef = useRef<string | null>(null);

  async function bootstrap() {
    setBusy(true);
    try {
      const sid = await ensureSpreadsheet();
      await Promise.all([ensurePicturesFolder(), ensureMapsFolder(), ensureAssetIcon()]);
      try {
        const wmId = await ensureWatermark();
        setWatermarkId(wmId);
        // Persist for fieldworkers, who resolve the watermark by id from the sheet.
        writeWatermarkFileId(sid, wmId).catch((e) => console.warn("persist watermark id:", e));
      } catch (e) { console.warn("ensureWatermark failed:", e); }
      setSpreadsheetId(sid);
      const data = await readAllRows(sid);
      setRows(data);
      try {
        const loaded = await readQrSettings(sid);
        localStorage.setItem("mew_qr_show_label", String(loaded.showLabel));
        localStorage.setItem("mew_qr_show_install_location", String(loaded.showInstallLocation));
        setQrSettings(loaded);
      } catch (e) { console.warn("readQrSettings failed:", e); }
      setLastSync(new Date());
      setStatus("");
    } catch (e) {
      notifyError(`${t("common.error")}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      setInitialLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { bootstrap(); }, []);

  async function handleCellSave(rowIndex: number, colIndex: number, value: string) {
    if (!spreadsheetId) return;
    if (colIndex === COL["表記名①"]) return;
    const oldId = rows[rowIndex][colIndex];
    try {
      await updateCell(spreadsheetId, rowIndex, colIndex, value);
      setRows((prev) => {
        const copy = prev.map((r) => [...r] as Row);
        copy[rowIndex][colIndex] = value;
        if (colIndex === COL["部局、施設名"] || colIndex === COL["階"]) {
          copy[rowIndex][COL["表記名①"]] = computeDisplayName(copy[rowIndex][COL["部局、施設名"]], copy[rowIndex][COL["階"]]);
        }
        return copy;
      });
      if (colIndex === COL["ID"] && oldId && oldId !== value) {
        // Photos stay linked via their stored file ids, so no Drive rename is needed (and
        // under drive.file the app can't rename files another account uploaded anyway).
        // Just remind the manager they can rename the files in Drive if they want them tidy.
        if (parsePhotoFileIds(rows[rowIndex][COL["写真ファイルID"]]).length > 0) {
          notify(t("sheet.renamePhotosManually", { from: String(oldId), to: value }), "info");
        }
      }
    } catch (e) {
      notifyError(t("sheet.saveError", { msg: (e as Error).message }));
    }
  }

  async function handleAddRow() {
    if (!spreadsheetId) return;
    const newRow: Row = HEADERS.map(() => "");
    newRow[0] = String(rows.length + 1);
    newRow[1] = "";
    newRow[COL["表記名①"]] = displayNameFormula(rows.length + 2);
    setBusy(true);
    try {
      await appendRows(spreadsheetId, [newRow]);
      const local = [...newRow] as Row;
      local[COL["表記名①"]] = computeDisplayName(local[COL["部局、施設名"]], local[COL["階"]]);
      setRows((r) => [...r, local]);
      notify(t("sheet.addedRow"));
    } catch (e) {
      notifyError(t("sheet.addError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  function handleCopyReportLink(id: string) {
    if (!id) return;
    const url = reportUrl(id, qrSettings.formUrl);
    if (!url) { notify(t("sheet.qrFormUrlRequired"), "info"); return; }
    navigator.clipboard.writeText(url).then(
      () => notify(t("sheet.reportUrlCopied", { id })),
      () => notifyError(t("sheet.copyFailed"))
    );
  }

  function toggleSelect(id: string) {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  async function handleGenerateQr() {
    if (selectedIds.size === 0) { notify(t("sheet.noRowsSelected"), "info"); return; }
    if (!qrSettings.formUrl.trim()) { notify(t("sheet.qrFormUrlRequired"), "info"); return; }
    setBusy(true);
    notify(t("sheet.qrGenerating"), "info");
    try {
      const selectedRows = rows.filter((r) => selectedIds.has(String(r[COL["ID"]] || "")));
      const items: QrItem[] = [];
      for (const r of selectedRows) {
        const id = String(r[COL["ID"]] || "");
        const url = reportUrl(id, qrSettings.formUrl);
        const label = String(r[COL["表記名①"]] || computeDisplayName(r[COL["部局、施設名"]], r[COL["階"]]) || "");
        const installLocation = String(r[COL["設置場所"]] || "");
        const svg = await buildQrCardSvg({ id, url, message: qrSettings.message, label, showLabel: qrSettings.showLabel, installLocation, showInstallLocation: qrSettings.showInstallLocation });
        items.push({ id, svg, label });
      }
      try { await writeQrSettings(spreadsheetId!, qrSettings); } catch (e) { console.warn("writeQrSettings failed:", e); }
      setQrItems(items);
      notify(t("sheet.qrGenerated", { count: String(items.length) }));
    } catch (e) {
      notifyError(t("sheet.qrError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  function handleAddPhotosClick(id: string) {
    if (!id) { notify(t("sheet.enterIdFirst"), "info"); return; }
    photoTargetIdRef.current = id;
    photoInputRef.current?.click();
  }

  async function handleDeleteRow(rowIndex: number, id: string) {
    if (!spreadsheetId) return;
    const label = id || `#${rowIndex + 1}`;
    if (!window.confirm(t("sheet.confirmDelete", { id: label }))) return;
    setBusy(true);
    notify(t("sheet.deletingRow", { id: label }), "info");
    try {
      const photoIds = parsePhotoFileIds(rows[rowIndex]?.[COL["写真ファイルID"]]);
      await deleteRow(spreadsheetId, rowIndex);
      await deletePhotosByIds(photoIds);
      setRows((prev) => prev.filter((_, i) => i !== rowIndex));
      notify(t("sheet.deletedRow", { id: label }));
    } catch (e) {
      notifyError(t("sheet.deleteError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteSelected() {
    if (!spreadsheetId || selectedIds.size === 0) return;
    const targets = rows.map((r, i) => ({ rowIndex: i, id: String(r[COL["ID"]] || ""), photoIds: parsePhotoFileIds(r[COL["写真ファイルID"]]) })).filter((x) => selectedIds.has(x.id));
    if (targets.length === 0) return;
    if (!window.confirm(t("sheet.confirmDeleteSelected", { count: String(targets.length) }))) return;
    setBusy(true);
    notify(t("sheet.deletingSelected", { count: String(targets.length) }), "info");
    try {
      const ordered = [...targets].sort((a, b) => b.rowIndex - a.rowIndex);
      for (const { rowIndex, photoIds } of ordered) {
        await deleteRow(spreadsheetId, rowIndex);
        await deletePhotosByIds(photoIds);
      }
      const removed = new Set(targets.map((x) => x.rowIndex));
      setRows((prev) => prev.filter((_, i) => !removed.has(i)));
      setSelectedIds(new Set());
      notify(t("sheet.deletedSelected", { count: String(targets.length) }));
    } catch (e) {
      notifyError(t("sheet.deleteError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
    }
  }

  async function handlePhotoFilesSelected(files: FileList | null) {
    const id = photoTargetIdRef.current;
    if (!id || !files || files.length === 0) return;
    setBusy(true);
    notify(t("sheet.uploadingPhotos", { id }), "info");
    try {
      let toUpload: Array<File | Blob> = Array.from(files);
      if (addWatermark && watermarkId) {
        const wmSrc = publicImageUrl(watermarkId);
        toUpload = await Promise.all(
          toUpload.map(async (f) => {
            try { return await applyWatermark(f as File, wmSrc); }
            catch (e) { console.warn("Watermark failed, uploading original:", e); return f; }
          })
        );
      }
      const rowIndex = rows.findIndex((r) => String(r[1] || "") === id);
      const prevPhotoIds = rowIndex >= 0 ? rows[rowIndex][COL["写真ファイルID"]] : "";
      const { fileIds } = await uploadDispenserPhotos(id, toUpload as File[], parsePhotoFileIds(prevPhotoIds).length);
      const merged = mergePhotoFileIds(prevPhotoIds, fileIds);
      if (rowIndex >= 0 && spreadsheetId) {
        await updateCell(spreadsheetId, rowIndex, COL["写真ファイルID"], merged);
        setRows((prev) => {
          const copy = prev.map((r) => [...r] as Row);
          copy[rowIndex][COL["写真ファイルID"]] = merged;
          return copy;
        });
      }
      notify(t("sheet.uploadedPhotos", { count: String(files.length) }));
    } catch (e) {
      notifyError(t("sheet.photoUploadError", { msg: (e as Error).message }));
    } finally {
      setBusy(false);
      photoTargetIdRef.current = null;
    }
  }

  const tableSide = (
    <>
      {initialLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 py-24 select-none">
          <div className="relative">
            <img
              src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`}
              alt="MeW"
              className="w-20 h-20 opacity-90"
            />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      ) : (
        <>
          {busy && (
            <div className="bg-blue-50 text-blue-700 text-sm px-3 py-1 flex items-center gap-2">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>{t("common.processing")}</span>
            </div>
          )}
          <EditableTable
            rows={rows}
            onCellSave={handleCellSave}
            onCopyReportLink={handleCopyReportLink}
            onAddPhotos={handleAddPhotosClick}
            onDeleteRow={handleDeleteRow}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
          />
        </>
      )}
    </>
  );

  const commandsSide = (
    <div className="p-4 space-y-3 overflow-y-auto bg-gray-50 h-full">
      <h2 className="font-bold text-lg flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-sliders text-gray-500" aria-hidden="true" />
        {t("sheet.commands")}
      </h2>
      <button className="w-full inline-flex items-center justify-center gap-2 bg-pink-600 text-white rounded-md px-3 py-2 hover:bg-pink-700 disabled:opacity-50 transition-colors" onClick={handleAddRow} disabled={busy || !spreadsheetId}>
        <i className="fa-solid fa-plus" aria-hidden="true" />
        <span>{t("sheet.addRow")}</span>
      </button>
      {selectedIds.size > 0 && (
        <button className="w-full inline-flex items-center justify-center gap-2 bg-red-600 text-white rounded-md px-3 py-2 hover:bg-red-700 disabled:opacity-50 transition-colors" onClick={handleDeleteSelected} disabled={busy || !spreadsheetId}>
          <i className="fa-solid fa-trash" aria-hidden="true" />
          <span>{t("sheet.deleteSelected", { count: String(selectedIds.size) })}</span>
        </button>
      )}
      <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoFilesSelected(e.target.files)} />
      <button className="w-full inline-flex items-center justify-center gap-2 bg-gray-700 text-white rounded-md px-3 py-2 hover:bg-gray-800 disabled:opacity-50 transition-colors" onClick={bootstrap} disabled={busy}>
        <i className="fa-solid fa-rotate" aria-hidden="true" />
        <span>{t("sheet.reload")}</span>
      </button>

      <div className="text-xs text-gray-600 pt-3 border-t space-y-1">
        {spreadsheetId && (
          <div>
            <a href={spreadsheetUrl(spreadsheetId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-700 hover:underline">
              <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
              <span>{t("sheet.openGoogleSheet")}</span>
            </a>
          </div>
        )}
        {lastSync && (
          <div className="flex items-center gap-1.5">
            <i className="fa-regular fa-clock text-gray-400" aria-hidden="true" />
            <span>{t("common.lastSync")}: {lastSync.toLocaleTimeString()}</span>
          </div>
        )}
        {status && (
          <div className={`flex items-start gap-1.5 ${statusTone === "error" ? "text-red-700" : statusTone === "success" ? "text-green-700" : "text-gray-700"}`}>
            <i className={`fa-solid mt-0.5 ${statusTone === "error" ? "fa-circle-exclamation" : statusTone === "success" ? "fa-circle-check" : "fa-circle-info"}`} aria-hidden="true" />
            <span>{status}</span>
          </div>
        )}
      </div>

      <div className="pt-3 mt-3 border-t space-y-2">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <i className="fa-solid fa-qrcode text-gray-500" aria-hidden="true" />
          {t("sheet.qrSettings")}
        </h3>
        <PersistentTextarea storageKey="qr-message" value={qrSettings.message} onChange={(e) => setQrSettings((s) => ({ ...s, message: e.target.value }))} placeholder={t("sheet.qrMessage")} rows={3} className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-y" />
        {(advanced || qrSettings.formId) && (
          <div className="flex items-center gap-2 flex-wrap">
            {advanced && (
              <Link to="/form" className="inline-flex items-center gap-1.5 text-xs text-indigo-700 hover:text-indigo-900">
                <i className="fa-solid fa-list-check" aria-hidden="true" />
                <span>{t("sheet.qrManageForm")}</span>
              </Link>
            )}
            {qrSettings.formId && (
              <a href={formEditUrl(qrSettings.formId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline">
                <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
                <span>{t("formSettings.openGoogleForm")}</span>
              </a>
            )}
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrColumns")}
            <input type="number" min={1} max={10} value={qrSettings.columns} onChange={(e) => setQrSettings((s) => ({ ...s, columns: Math.max(1, parseInt(e.target.value, 10) || 1) }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </label>
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrRows")}
            <input type="number" min={0} max={20} value={qrSettings.rows} onChange={(e) => setQrSettings((s) => ({ ...s, rows: Math.max(0, parseInt(e.target.value, 10) || 0) }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </label>
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrWidth")}
            <input type="number" min={100} max={1000} step={50} value={qrSettings.width} onChange={(e) => setQrSettings((s) => ({ ...s, width: Math.max(100, parseInt(e.target.value, 10) || 100) }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </label>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrPageSize")}
            <select value={qrSettings.pageSize} onChange={(e) => setQrSettings((s) => ({ ...s, pageSize: e.target.value }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              {QR_PAGE_SIZES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrOrientation")}
            <select value={qrSettings.orientation} onChange={(e) => setQrSettings((s) => ({ ...s, orientation: e.target.value }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              {QR_ORIENTATIONS.map((o) => <option key={o} value={o}>{t(`sheet.qrOrientation_${o}`)}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrMargin")}
            <input type="number" min={0} max={50} value={qrSettings.margin} onChange={(e) => setQrSettings((s) => ({ ...s, margin: Math.max(0, parseInt(e.target.value, 10) || 0) }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </label>
          <label className="flex-1 text-sm text-gray-700">{t("sheet.qrGap")}
            <input type="number" min={0} max={50} value={qrSettings.gap} onChange={(e) => setQrSettings((s) => ({ ...s, gap: Math.max(0, parseInt(e.target.value, 10) || 0) }))} className="mt-1 w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={qrSettings.showLabel} onChange={(e) => { localStorage.setItem("mew_qr_show_label", String(e.target.checked)); setQrSettings((s) => ({ ...s, showLabel: e.target.checked })); }} />
          <span>{t("sheet.qrShowLabel")}</span>
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={qrSettings.showInstallLocation} onChange={(e) => { localStorage.setItem("mew_qr_show_install_location", String(e.target.checked)); setQrSettings((s) => ({ ...s, showInstallLocation: e.target.checked })); }} />
          <span>{t("sheet.qrShowInstallLocation")}</span>
        </label>
        <button className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 text-white rounded-md px-3 py-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors" onClick={handleGenerateQr} disabled={busy || !spreadsheetId || selectedIds.size === 0}>
          <i className="fa-solid fa-qrcode" aria-hidden="true" />
          <span>{t("sheet.generateQr", { count: String(selectedIds.size) })}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-3.5rem)] relative">
      {busy && !initialLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 select-none">
            <div className="relative">
              <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
              <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
            </div>
            <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
          </div>
        </div>
      )}
      <div className="absolute top-1 right-2 z-20 bg-white/80 backdrop-blur px-2 py-1 rounded">{slider}</div>
      <div className="h-full mx-auto" style={{ maxWidth }}>
        <div className="hidden md:block h-full">
          <ResizableSplit storageKey="sheetview" initialPercent={65}>
            {tableSide}
            {commandsSide}
          </ResizableSplit>
        </div>
        <div className="md:hidden flex flex-col h-full">
          <div className="flex-1 min-h-0 flex flex-col">{tableSide}</div>
          <div className="border-t max-h-60 overflow-y-auto shrink-0">{commandsSide}</div>
        </div>
      </div>
      {qrItems && (
        <QrPrintModal
          items={qrItems}
          columns={qrSettings.columns}
          rows={qrSettings.rows}
          width={qrSettings.width}
          pageSize={qrSettings.pageSize}
          orientation={qrSettings.orientation}
          margin={qrSettings.margin}
          gap={qrSettings.gap}
          onClose={() => setQrItems(null)}
        />
      )}
    </div>
  );
}
